import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshPhysicalNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `MeshPhysicalNodeMaterial` asset: physical PBR with serialized metal/rough plus transmission and thickness. */
@EngineClass('MeshPhysicalNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Mesh Physical Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class MeshPhysicalNodeMaterialAsset extends NodeMaterialAsset(MeshPhysicalNodeMaterial) {

  @property({ type: 'color', description: 'Diffuse color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Metalness' })
  override metalness = 0.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Roughness' })
  override roughness = 0.5;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Transmission' })
  override transmission = 0.0;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Thickness' })
  override thickness = 0.0;

  @property({ type: 'boolean', description: 'Enable flat shading' })
  override flatShading = false;

  @property({ type: 'color', description: 'Emissive color' })
  override emissive = new THREE.Color(0.0, 0.0, 0.0);

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Emissive intensity' })
  override emissiveIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Clearcoat amount' })
  override clearcoat = 0.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Clearcoat roughness' })
  override clearcoatRoughness = 0.0;

  @property({ type: 'number', min: 1, max: 2.333, step: 0.01, description: 'Index of refraction' })
  override ior = 1.5;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Iridescence amount' })
  override iridescence = 0.0;

  @property({ type: 'number', min: 1, max: 2.333, step: 0.01, description: 'Iridescence IOR' })
  override iridescenceIOR = 1.3;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Sheen amount' })
  override sheen = 0.0;

  @property({ type: 'color', description: 'Sheen color' })
  override sheenColor = new THREE.Color(0.0, 0.0, 0.0);

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Sheen roughness' })
  override sheenRoughness = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Specular intensity' })
  override specularIntensity = 1.0;

  @property({ type: 'color', description: 'Specular color' })
  override specularColor = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Anisotropy amount' })
  override anisotropy = 0.0;

  @property({ type: 'number', min: 0, max: Math.PI * 2, step: 0.01, description: 'Anisotropy rotation in radians' })
  override anisotropyRotation = 0.0;

  @property({ type: 'color', description: 'Attenuation color' })
  override attenuationColor = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0, max: 1000, step: 0.1, description: 'Attenuation distance' })
  override attenuationDistance = Infinity;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Ambient occlusion map intensity' })
  override aoMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Light map intensity' })
  override lightMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Environment reflectivity' })
  override reflectivity = 0.5;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Refraction ratio' })
    refractionRatio = 0.98;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Environment map intensity' })
  override envMapIntensity = 1.0;

  @property({ type: 'number', min: -10, max: 10, step: 0.01, description: 'Bump map influence' })
  override bumpScale = 1.0;

  @property({ type: 'vector2', description: 'Normal map influence' })
  override normalScale = new THREE.Vector2(1, 1);

  @property({ type: 'number', description: 'Normal map type (`THREE.TangentSpaceNormalMap`, `ObjectSpaceNormalMap`)' })
  override normalMapType = THREE.TangentSpaceNormalMap;

  @property({ type: 'texturePath', description: 'Color map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Alpha map path' })
    alphaMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Roughness map path' })
    roughnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Metalness map path' })
    metalnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Normal map path' })
    normalMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Bump map path' })
    bumpMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Displacement map path' })
    displacementMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Emissive map path' })
    emissiveMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Clearcoat map path' })
    clearcoatMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Clearcoat roughness map path' })
    clearcoatRoughnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Clearcoat normal map path' })
    clearcoatNormalMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Transmission map path' })
    transmissionMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Thickness map path' })
    thicknessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Sheen color map path' })
    sheenColorMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Sheen roughness map path' })
    sheenRoughnessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Iridescence map path' })
    iridescenceMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Iridescence thickness map path' })
    iridescenceThicknessMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Specular intensity map path' })
    specularIntensityMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Specular color map path' })
    specularColorMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Environment map path' })
    envMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Ambient occlusion map path' })
    aoMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Light map path' })
    lightMapPath: TexturePath = '';

  private readonly _metalnessUniform = TSL.uniform(this.metalness);
  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _transmissionUniform = TSL.uniform(this.transmission);
  private readonly _thicknessUniform = TSL.uniform(this.thickness);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.metalness = this.clampFiniteNumber(this.metalness, 0, 1, 0.0);
    this.roughness = this.clampFiniteNumber(this.roughness, 0, 1, 0.5);
    this.transmission = this.clampFiniteNumber(this.transmission, 0, 1, 0.0);
    this.thickness = this.floorFiniteNumber(this.thickness, 0, 0.0);
    this.clearcoat = this.clampFiniteNumber(this.clearcoat, 0, 1, 0.0);
    this.clearcoatRoughness = this.clampFiniteNumber(this.clearcoatRoughness, 0, 1, 0.0);
    this.ior = this.clampFiniteNumber(this.ior, 1, 2.333, 1.5);
    this.iridescence = this.clampFiniteNumber(this.iridescence, 0, 1, 0.0);
    this.iridescenceIOR = this.clampFiniteNumber(this.iridescenceIOR, 1, 2.333, 1.3);
    this.sheen = this.clampFiniteNumber(this.sheen, 0, 1, 0.0);
    this.sheenRoughness = this.clampFiniteNumber(this.sheenRoughness, 0, 1, 1.0);
    this.specularIntensity = this.clampFiniteNumber(this.specularIntensity, 0, 1, 1.0);
    this.anisotropy = this.clampFiniteNumber(this.anisotropy, 0, 1, 0.0);
    this.anisotropyRotation = this.clampFiniteNumber(this.anisotropyRotation, 0, Math.PI * 2, 0.0);
    this.attenuationDistance = this.floorFiniteNumber(this.attenuationDistance, 0, Infinity);
    this.aoMapIntensity = this.clampFiniteNumber(this.aoMapIntensity, 0, 1, 1.0);
    this.lightMapIntensity = this.floorFiniteNumber(this.lightMapIntensity, 0, 1.0);
    this.reflectivity = this.clampFiniteNumber(this.reflectivity, 0, 1, 0.5);
    this.refractionRatio = this.clampFiniteNumber(this.refractionRatio, 0, 1, 0.98);
    this.emissiveIntensity = this.floorFiniteNumber(this.emissiveIntensity, 0, 1.0);
    this.envMapIntensity = this.floorFiniteNumber(this.envMapIntensity, 0, 1.0);
    this.bumpScale = this.clampFiniteNumber(this.bumpScale, -10, 10, 1.0);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace);
    this.syncTexturePath('roughnessMap', this.roughnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('metalnessMap', this.metalnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('normalMap', this.normalMapPath, THREE.NoColorSpace);
    this.syncTexturePath('bumpMap', this.bumpMapPath, THREE.NoColorSpace);
    this.syncTexturePath('displacementMap', this.displacementMapPath, THREE.NoColorSpace);
    this.syncTexturePath('emissiveMap', this.emissiveMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('clearcoatMap', this.clearcoatMapPath, THREE.NoColorSpace);
    this.syncTexturePath('clearcoatRoughnessMap', this.clearcoatRoughnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('clearcoatNormalMap', this.clearcoatNormalMapPath, THREE.NoColorSpace);
    this.syncTexturePath('transmissionMap', this.transmissionMapPath, THREE.NoColorSpace);
    this.syncTexturePath('thicknessMap', this.thicknessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('sheenColorMap', this.sheenColorMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('sheenRoughnessMap', this.sheenRoughnessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('iridescenceMap', this.iridescenceMapPath, THREE.NoColorSpace);
    this.syncTexturePath('iridescenceThicknessMap', this.iridescenceThicknessMapPath, THREE.NoColorSpace);
    this.syncTexturePath('specularIntensityMap', this.specularIntensityMapPath, THREE.NoColorSpace);
    this.syncTexturePath('specularColorMap', this.specularColorMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('envMap', this.envMapPath, THREE.NoColorSpace);
    this.syncTexturePath('aoMap', this.aoMapPath, THREE.NoColorSpace);
    this.syncTexturePath('lightMap', this.lightMapPath, THREE.NoColorSpace);
    this.colorNode = null;
    this._metalnessUniform.value = this.metalness;
    this._roughnessUniform.value = this.roughness;
    this._transmissionUniform.value = this.transmission;
    this._thicknessUniform.value = this.thickness;
    this.metalnessNode = this._metalnessUniform;
    this.roughnessNode = this._roughnessUniform;
    (this as any).transmissionNode = this._transmissionUniform;
    (this as any).thicknessNode = this._thicknessUniform;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): MeshPhysicalNodeMaterialAsset {
    const instance = new MeshPhysicalNodeMaterialAsset();
    MeshPhysicalNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
